import React, { createContext, useContext, useEffect, useState } from "react";

interface LandingContextType {
  utmId: string;
  utmSource: string;
  referredBy: string;
}

const LandingContext = createContext<LandingContextType | undefined>(undefined);

const UTM_ID_STORAGE_KEY = "landingUtmId";
const UTM_SOURCE_STORAGE_KEY = "landingUtmSource";
const REFERRED_BY_STORAGE_KEY = "landingReferredBy";
const TRACKED_QUERY_PARAMS = new Set([
  "utm_id",
  "utm_source",
  "referred_by",
]);

const readStoredValue = (key: string) => {
  if (typeof window === "undefined") {
    return "";
  }

  return sessionStorage.getItem(key) || "";
};

export const useLandingContext = () => {
  const context = useContext(LandingContext);

  if (context === undefined) {
    throw new Error("useLandingContext must be used within a LandingProvider");
  }

  return context;
};

export const LandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [utmId, setUtmId] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const queryUtmId = new URLSearchParams(window.location.search).get("utm_id");
    return queryUtmId || readStoredValue(UTM_ID_STORAGE_KEY);
  });

  const [utmSource, setUtmSource] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const queryUtmSource = new URLSearchParams(window.location.search).get("utm_source");
    return queryUtmSource || readStoredValue(UTM_SOURCE_STORAGE_KEY);
  });

  const [referredBy, setReferredBy] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const queryReferredBy = new URLSearchParams(window.location.search).get("referred_by");
    return queryReferredBy || readStoredValue(REFERRED_BY_STORAGE_KEY);
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const queryUtmId = url.searchParams.get("utm_id") || "";
    const queryUtmSource = url.searchParams.get("utm_source") || "";
    const queryReferredBy = url.searchParams.get("referred_by") || "";

    if (queryUtmId) {
      setUtmId(queryUtmId);
      sessionStorage.setItem(UTM_ID_STORAGE_KEY, queryUtmId);
    }

    if (queryUtmSource) {
      setUtmSource(queryUtmSource);
      sessionStorage.setItem(UTM_SOURCE_STORAGE_KEY, queryUtmSource);
    }

    if (queryReferredBy) {
      setReferredBy(queryReferredBy);
      sessionStorage.setItem(REFERRED_BY_STORAGE_KEY, queryReferredBy);
    }

    const currentQueryParams = Array.from(url.searchParams.keys());
    let didRemoveUnknownParams = false;

    currentQueryParams.forEach((key) => {
      if (!TRACKED_QUERY_PARAMS.has(key)) {
        url.searchParams.delete(key);
        didRemoveUnknownParams = true;
      }
    });

    if (queryUtmId || queryUtmSource || queryReferredBy || didRemoveUnknownParams) {
      const nextSearch = url.searchParams.toString();
      const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`;
      window.history.replaceState({}, "", nextUrl);
    }
  }, []);

  return (
    <LandingContext.Provider value={{ utmId, utmSource, referredBy }}>
      {children}
    </LandingContext.Provider>
  );
};
